/**
 * Admin Advisors API Service
 * HTTP client methods for financial advisor management endpoints
 */

import { apiClient } from '@/core/api/client';
import type { PaginatedResponse } from '../types/pagination.type';
import type {
  AdminAdvisor,
  AdminAdvisorEditorDetail,
  AdminAdvisorEditorUpdatePayload,
  UpdateAdvisorPayload,
  AdvisorListParams,
} from '../types/admin-advisor.type';

const BASE_PATH = '/admin/advisors';

/**
 * List financial advisors with pagination and search
 * GET /admin/advisors
 * 
 * @param params - Query parameters (page, pageSize, search)
 * @returns Paginated list of advisors with total count reflecting filters
 */
export async function listAdvisors(
  params?: AdvisorListParams,
): Promise<PaginatedResponse<AdminAdvisor>> {
  const response = await apiClient.get<PaginatedResponse<AdminAdvisor>>(
    BASE_PATH,
    { params },
  );
  return response.data;
}

/**
 * Get single advisor by ID
 * GET /admin/advisors/:id
 * 
 * @param id - Advisor ID
 * @returns Advisor details with user info and client counts
 */
export async function getAdvisor(id: string): Promise<AdminAdvisor> {
  const response = await apiClient.get<AdminAdvisor>(`${BASE_PATH}/${id}`);
  return response.data;
}

export async function getAdvisorEditorDetail(
  id: string,
): Promise<AdminAdvisorEditorDetail> {
  const response = await apiClient.get<AdminAdvisorEditorDetail>(`${BASE_PATH}/${id}`);
  return response.data;
}

/**
 * Update existing advisor
 * PATCH /admin/advisors/:id
 * 
 * @param id - Advisor ID
 * @param payload - Partial advisor data to update
 * @returns Updated advisor
 */
export async function updateAdvisor(
  id: string,
  payload: UpdateAdvisorPayload,
): Promise<AdminAdvisor> {
  const response = await apiClient.patch<AdminAdvisor>(
    `${BASE_PATH}/${id}`,
    payload,
  );
  return response.data;
}

export async function saveAdvisorEditor(
  id: string,
  payload: AdminAdvisorEditorUpdatePayload,
): Promise<AdminAdvisorEditorDetail> {
  const response = await apiClient.patch<AdminAdvisorEditorDetail>(
    `${BASE_PATH}/${id}`,
    payload,
  );
  return response.data;
}

/**
 * Soft-delete advisor
 * DELETE /admin/advisors/:id
 * 
 * @param id - Advisor ID
 * @returns void (204 No Content)
 */
export async function softDeleteAdvisor(id: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${id}`);
}

/**
 * Feature 100 — trial lifecycle actions (admin-only).
 */
export type AdvisorTrialState = {
  id: string;
  isTrial: boolean;
  trialStartedAt: string | null;
  trialExpiresAt: string | null;
  trialConvertedAt: string | null;
};

export async function startAdvisorTrial(id: string, days?: number): Promise<AdvisorTrialState> {
  const response = await apiClient.post<AdvisorTrialState>(
    `${BASE_PATH}/${id}/trial`,
    days ? { days } : {},
  );
  return response.data;
}

export async function extendAdvisorTrial(id: string, days: number): Promise<AdvisorTrialState> {
  const response = await apiClient.post<AdvisorTrialState>(`${BASE_PATH}/${id}/trial/extend`, {
    days,
  });
  return response.data;
}

export async function convertAdvisorTrial(id: string): Promise<AdvisorTrialState> {
  const response = await apiClient.post<AdvisorTrialState>(`${BASE_PATH}/${id}/trial/convert`, {});
  return response.data;
}

export async function endAdvisorTrial(id: string): Promise<AdvisorTrialState> {
  const response = await apiClient.post<AdvisorTrialState>(`${BASE_PATH}/${id}/trial/end`, {});
  return response.data;
}

/**
 * Default export for convenient importing
 */
export const adminAdvisorsApi = {
  listAdvisors,
  getAdvisor,
  getAdvisorEditorDetail,
  updateAdvisor,
  saveAdvisorEditor,
  softDeleteAdvisor,
  startAdvisorTrial,
  extendAdvisorTrial,
  convertAdvisorTrial,
  endAdvisorTrial,
};
